// ResponsiveModal - Full-screen on mobile, floating on desktop
import React from 'react';
import { X } from 'lucide-react';

interface ResponsiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
}

export default function ResponsiveModal({ 
  isOpen, 
  onClose, 
  title, 
  children,
  maxWidth = 'md'
}: ResponsiveModalProps) {
  if (!isOpen) return null;

  const maxWidthClasses = {
    sm: 'md:max-w-sm',
    md: 'md:max-w-md',
    lg: 'md:max-w-lg',
    xl: 'md:max-w-xl'
  };

  return (
    <div className="fixed inset-0 z-[10000] flex md:items-center md:justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Modal - Full screen on mobile, floating on desktop */}
      <div className={`
        relative w-full h-full
        bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900
        shadow-2xl
        flex flex-col
        
        /* Desktop: Floating modal */
        md:h-auto md:max-h-[90vh]
        md:rounded-2xl md:border md:border-slate-700
        ${maxWidthClasses[maxWidth]}
      `}>
        {/* Header - Fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 md:p-6 border-b border-slate-700 bg-slate-900/80 backdrop-blur-xl">
          <button
            onClick={onClose}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors touch-manipulation md:hidden"
          >
            <span className="text-2xl">←</span>
            <span className="text-sm font-semibold">Back</span>
          </button>
          <h2 className="text-xl md:text-2xl font-bold text-white flex-1 md:flex-none text-center md:text-left">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors touch-manipulation hidden md:block"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Scrollable, fills remaining space */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </div>
      </div>
    </div>
  );
}
